import { useState, type FormEvent } from 'react';
import { useT, type Messages } from '~/i18n';
import type { PieceKind, PieceNote, ProfilePiece } from '../api/profileClient';

/**
 * Una pieza de «Mis datos»: un trabajo, un estudio, una licencia...
 *
 * Arriba va cómo sale en la hoja de vida (las viñetas, en inglés) y debajo las
 * palabras de la persona, nota por nota. Las notas `recovered` salieron de una
 * versión anterior del cuestionario: van marcadas y se pueden borrar, porque
 * puede ser justo lo que la persona había corregido a propósito.
 *
 * Borrar confirma EN LA TARJETA, con dos toques: nada de `window.confirm`, que
 * en un teléfono se ve como un error del navegador y no como una pregunta.
 */

type Copy = Messages['onboarding']['improveCV'];
type FieldKey = keyof Copy['fields'];

interface FieldSpec {
  key: Exclude<FieldKey, 'stillThere' | 'text'>;
  type: 'text' | 'year' | 'list';
  required?: boolean;
}

/** Los campos que se editan por tipo; son las llaves de `fields` del servicio. */
const FORM_FIELDS: Record<PieceKind, FieldSpec[]> = {
  job: [
    { key: 'company', type: 'text', required: true },
    { key: 'role', type: 'text', required: true },
    { key: 'start_year', type: 'year', required: true },
    { key: 'end_year', type: 'year' },
  ],
  education: [
    { key: 'level', type: 'text', required: true },
    { key: 'institution', type: 'text' },
    { key: 'city', type: 'text' },
    { key: 'year', type: 'year' },
  ],
  license: [
    { key: 'type', type: 'text', required: true },
    { key: 'country', type: 'text' },
    { key: 'since_year', type: 'year' },
  ],
  certification: [
    { key: 'name', type: 'text', required: true },
    { key: 'issuer', type: 'text' },
    { key: 'year', type: 'year' },
  ],
  skills: [{ key: 'items', type: 'list' }],
  // Los idiomas llevan nivel por idioma: se cuentan en palabras y el modelo los ordena.
  languages: [],
  physical: [],
  other: [{ key: 'label', type: 'text', required: true }],
};

const MAX_TEXT = 4000;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');

/**
 * El cuestionario guarda el estudio contado en palabras con el nivel 'declared':
 * es una marca del servicio, nunca algo que la persona escribió. No se muestra
 * ni se precarga para editar.
 */
const DECLARED_LEVEL = 'declared';
const levelOf = (f: Record<string, unknown>): string => (str(f.level) === DECLARED_LEVEL ? '' : str(f.level));

/** El comienzo de lo que contó, para nombrar la pieza cuando no hay campo que lo haga. */
function shortText(text: string, max = 80): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > 40 ? cut.slice(0, space) : cut).trim()}…`;
}

/** El nombre de la pieza: lo que la persona reconoce de un vistazo. */
export function pieceTitle(piece: ProfilePiece, t: Copy): string {
  const f = piece.fields ?? {};
  const join = (...parts: unknown[]) => parts.map(str).filter(Boolean).join(' · ');
  const title =
    piece.kind === 'job'
      ? join(f.role, f.company)
      : piece.kind === 'education'
        ? join(levelOf(f) || shortText(piece.text ?? ''), f.institution)
        : piece.kind === 'license'
          ? join(f.type, f.country)
          : piece.kind === 'certification'
            ? join(f.name, f.issuer)
            : piece.kind === 'other'
              ? join(f.label)
              : '';
  return title || t.kinds[piece.kind];
}

/** «2019-2023», «2019-Actual», o nada si no hay años. */
export function period(piece: ProfilePiece, t: Copy): string | null {
  const f = piece.fields ?? {};
  if (piece.kind === 'job') {
    const start = str(f.start_year);
    if (!start) return null;
    if (f.end_year === null) return `${start}-${t.current}`;
    const end = str(f.end_year);
    return end && end !== start ? `${start}-${end}` : start;
  }
  return str(f.year) || str(f.since_year) || null;
}

/** Las listas (habilidades, idiomas) se ven como pastillas. */
function listItems(piece: ProfilePiece): string[] {
  const items = (piece.fields as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  return items
    .map((it) => {
      if (typeof it === 'string') return it.trim();
      if (!it || typeof it !== 'object') return '';
      // Idiomas: «Inglés (básico)».
      const { language, level } = it as { language?: unknown; level?: unknown };
      return str(level) ? `${str(language)} (${str(level)})` : str(language);
    })
    .filter(Boolean);
}

/** «Estamos actualizando tu hoja de vida», en una línea: va adentro de la tarjeta. */
export function SavingLine({ label }: { label: string }) {
  return (
    <p className="text-sm hush flex items-center gap-2" role="status">
      <span className="loading loading-spinner loading-sm" aria-hidden="true" />
      {label}
    </p>
  );
}

// ─── El formulario de agregar y editar ──────────────────────────────────────

interface FormProps {
  kind: PieceKind;
  /** Ausente al agregar. */
  piece?: ProfilePiece;
  /** El nombre accesible del formulario. */
  label: string;
  saving: boolean;
  disabled: boolean;
  error?: string | null;
  /** Al agregar llega todo; al editar, solo lo que cambió (vacío = nada que guardar). */
  onSubmit: (patch: { fields?: Record<string, unknown>; text?: string }) => void;
  onCancel: () => void;
}

export function PieceForm({ kind, piece, label, saving, disabled, error, onSubmit, onCancel }: FormProps) {
  const t = useT('onboarding').improveCV;
  const specs = FORM_FIELDS[kind];
  const initial = piece?.fields ?? {};
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      specs.map((s) => [
        s.key,
        s.type === 'list' && Array.isArray(initial[s.key])
          ? (initial[s.key] as unknown[]).map(str).join(', ')
          : s.key === 'level'
            ? levelOf(initial)
            : str(initial[s.key]),
      ]),
    ),
  );
  const [stillThere, setStillThere] = useState(kind === 'job' && initial.end_year === null);
  /** Tocó «Todavía trabajo aquí»: al destildarlo, el año de fin deja de ser opcional. */
  const [touchedStill, setTouchedStill] = useState(false);
  const [text, setText] = useState(piece?.text ?? '');
  const idFor = (key: string) => `piece-${piece?.id ?? 'new'}-${key}`;

  /** Los `fields` como los pide el servicio: años en número, listas en arreglo, vacíos fuera. */
  const built = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const s of specs) {
      const raw = (values[s.key] ?? '').trim();
      if (s.key === 'end_year' && stillThere) {
        out.end_year = null;
        continue;
      }
      if (s.type === 'year') {
        const n = Number.parseInt(raw, 10);
        if (raw && Number.isFinite(n)) out[s.key] = n;
      } else if (s.type === 'list') {
        const items = raw.split(',').map((x) => x.trim()).filter(Boolean);
        if (items.length || Array.isArray(initial[s.key])) out[s.key] = items;
      } else if (raw) {
        out[s.key] = raw;
      }
    }
    return out;
  };

  const yearOk = (raw: string) => !raw.trim() || /^(19|20|21)\d{2}$/.test(raw.trim());
  // Quien destilda «Todavía trabajo aquí» está diciendo que ya terminó: sin el
  // año, el trabajo quedaría con un fin desconocido en vez del que acaba de dar.
  const needsEndYear =
    kind === 'job' && !stillThere && touchedStill && !(values.end_year ?? '').trim();
  const valid = !needsEndYear && specs.every(
    (s) =>
      (!s.required || (values[s.key] ?? '').trim()) &&
      (s.type !== 'year' || (s.key === 'end_year' && stillThere) || yearOk(values[s.key] ?? '')),
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || disabled) return;
    const fields = built();
    if (!piece) return onSubmit({ fields, text: text.trim() });
    // Editar: solo las llaves que cambiaron. Guardar lo mismo no es un cambio.
    const changed = Object.fromEntries(
      Object.entries(fields).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(initial[k])),
    );
    const patch: { fields?: Record<string, unknown>; text?: string } = {};
    if (Object.keys(changed).length) patch.fields = changed;
    if (text.trim() !== (piece.text ?? '').trim()) patch.text = text.trim();
    onSubmit(patch);
  };

  return (
    <form aria-label={label} className="space-y-3" onSubmit={submit}>
      {specs.map((s) => (
        <div key={s.key} className="space-y-1">
          <label className="label pb-0" htmlFor={idFor(s.key)}>
            <span className="label-text font-medium">{s.type === 'list' ? t.kinds[kind] : t.fields[s.key]}</span>
          </label>
          <input
            id={idFor(s.key)}
            className="input w-full"
            inputMode={s.type === 'year' ? 'numeric' : undefined}
            maxLength={s.type === 'year' ? 4 : s.type === 'list' ? 2000 : 200}
            placeholder={s.type === 'list' ? t.fields.items : undefined}
            value={values[s.key] ?? ''}
            disabled={s.key === 'end_year' && stillThere}
            onChange={(e) => setValues((v) => ({ ...v, [s.key]: e.target.value }))}
          />
          {s.key === 'end_year' && needsEndYear && (
            <p className="text-xs text-friction" role="status">
              {t.endYearNeeded}
            </p>
          )}
        </div>
      ))}

      {kind === 'job' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={stillThere}
            onChange={(e) => {
              setStillThere(e.target.checked);
              setTouchedStill(true);
            }}
          />
          {t.fields.stillThere}
        </label>
      )}

      <div className="space-y-1">
        <label className="label pb-0" htmlFor={idFor('text')}>
          <span className="label-text font-medium">{t.fields.text}</span>
        </label>
        <textarea
          id={idFor('text')}
          className="textarea w-full"
          rows={3}
          maxLength={MAX_TEXT}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      {error && <div className="alert alert-error text-sm">{error}</div>}
      {saving && <SavingLine label={t.saving} />}

      <div className="flex gap-3">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          {t.cancel}
        </button>
        <button type="submit" className="btn btn-primary flex-1" disabled={!valid || disabled}>
          {t.save}
        </button>
      </div>
    </form>
  );
}

// ─── La confirmación en el lugar ────────────────────────────────────────────

function InlineConfirm({
  onConfirm,
  onCancel,
  disabled,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  disabled: boolean;
}) {
  const t = useT('onboarding').improveCV;
  return (
    <div className="flex flex-wrap items-center gap-2" role="group">
      <span className="text-sm text-friction flex-1 min-w-[10rem]">{t.confirmDelete}</span>
      <button type="button" className="btn btn-sm btn-error" onClick={onConfirm} disabled={disabled}>
        {t.delete}
      </button>
      <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel} disabled={disabled}>
        {t.cancel}
      </button>
    </div>
  );
}

// ─── La tarjeta ─────────────────────────────────────────────────────────────

interface CardProps {
  piece: ProfilePiece;
  /** Esta tarjeta está guardando. */
  saving: boolean;
  /** Otra escritura en curso: los botones esperan. */
  disabled: boolean;
  error?: string | null;
  /** Resuelven true si se guardó: la tarjeta cierra el formulario o la confirmación. */
  onEdit: (patch: { fields?: Record<string, unknown>; text?: string }) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  onDeleteNote: (noteId: string) => Promise<boolean>;
}

export default function ProfilePieceCard({ piece, saving, disabled, error, onEdit, onDelete, onDeleteNote }: CardProps) {
  const t = useT('onboarding').improveCV;
  const [editing, setEditing] = useState(false);
  /** 'piece' o el id de la nota que se está por borrar. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const title = pieceTitle(piece, t);
  const when = period(piece, t);
  const preview = (piece.rendered_preview ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  const items = listItems(piece);
  const busy = saving || disabled;

  const removeNote = async (note: PieceNote) => {
    if (await onDeleteNote(note.id)) setConfirming(null);
  };

  return (
    <section aria-label={title} className="card">
      <div className="card-body space-y-3">
        <header className="space-y-1">
          <span className="brand-label">{t.kinds[piece.kind]}</span>
          <h3 className="font-semibold">{title}</h3>
          {when && <p className="text-xs hush">{when}</p>}
          {piece.source === 'recovered' && <span className="chip">{t.recovered}</span>}
        </header>

        {editing ? (
          <PieceForm
            kind={piece.kind}
            piece={piece}
            label={`${t.edit}: ${title}`}
            saving={saving}
            disabled={busy}
            error={error}
            onCancel={() => setEditing(false)}
            onSubmit={async (patch) => {
              if (!patch.fields && patch.text === undefined) return setEditing(false);
              if (await onEdit(patch)) setEditing(false);
            }}
          />
        ) : (
          <>
            {items.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {items.map((it) => (
                  <span key={it} className="chip">
                    {it}
                  </span>
                ))}
              </div>
            )}

            {preview.length > 0 ? (
              <div className="space-y-1">
                <span className="brand-label">{t.preview}</span>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {preview.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : (
              piece.text.trim() && <p className="text-sm">{piece.text}</p>
            )}

            {piece.notes.length > 0 && (
              <div className="space-y-1">
                <span className="brand-label">{t.notes}</span>
                <ul className="space-y-2">
                  {piece.notes.map((note) => (
                    <li key={note.id} className="text-sm space-y-1">
                      <p>{note.text}</p>
                      {note.source === 'recovered' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="chip">{t.recovered}</span>
                          {confirming === note.id ? (
                            <InlineConfirm
                              disabled={busy}
                              onConfirm={() => void removeNote(note)}
                              onCancel={() => setConfirming(null)}
                            />
                          ) : (
                            <button
                              type="button"
                              className="btn btn-xs btn-ghost"
                              aria-label={`${t.deleteNote}: ${title}`}
                              disabled={busy}
                              onClick={() => setConfirming(note.id)}
                            >
                              {t.deleteNote}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error && <div className="alert alert-error text-sm">{error}</div>}
            {saving && <SavingLine label={t.saving} />}

            {confirming === 'piece' ? (
              <InlineConfirm
                disabled={busy}
                onConfirm={() => void onDelete().then((ok) => ok && setConfirming(null))}
                onCancel={() => setConfirming(null)}
              />
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  aria-label={`${t.edit}: ${title}`}
                  disabled={busy}
                  onClick={() => {
                    setConfirming(null);
                    setEditing(true);
                  }}
                >
                  {t.edit}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  aria-label={`${t.delete}: ${title}`}
                  disabled={busy}
                  onClick={() => setConfirming('piece')}
                >
                  {t.delete}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
